import { css } from 'emotion';
import { observer } from 'mobx-react';
import React, { useState } from 'react';

import { Button } from '../../../../../components/Button';
import { ButtonGroup } from '../../../../../components/ButtonGroup';
import { Caption, CaptionGroup } from '../../../../../components/Caption';
import { Card } from '../../../../../components/Card';
import { DocumentTitle } from '../../../../../components/DocumentTitle';
import { ErrorMessage } from '../../../../../components/ErrorMessage';
import { FormItem, FormLabel, FormLayout, FormSubLabel } from '../../../../../components/Form';
import { Modal } from '../../../../../components/Modal';
import { PhoneInput } from '../../../../../components/PhoneInput';
import { Wrapper } from '../../../../../components/Wrapper';
import { Color } from '../../../../../constants/Style';
import { useTransaction } from '../../../../../hooks/useTransaction';
import { AccountDetailStore } from '../../stores/AccountDetailStore';
import { AddressInput } from '../components/AddressInput';
import { InsuranceCardInput } from '../components/InsuranceCardInput';

export type FormDirtyItem = {
  phone: boolean;
  addressLine1: boolean;
  addressLine2: boolean;
  pref: boolean;
  zipCode: boolean;
};

type Props = {
  store: AccountDetailStore;
  onCommit: () => void;
  disabled?: boolean;
};
export const RegisterPage = observer(({ store, onCommit, disabled }: Props) => {
  const [dirty, setDirty] = useState<FormDirtyItem>({
    phone: false,
    addressLine1: false,
    addressLine2: false,
    pref: false,
    zipCode: false,
  });

  const onFocusOut = (target: string) => {
    setDirty({
      ...dirty,
      [target]: true,
    });
  };

  const [validateProfile, validateStatus] = useTransaction(async () => {
    setDirty({
      phone: true,
      addressLine1: true,
      addressLine2: true,
      pref: true,
      zipCode: true,
    });
    store.edit?.firstReserveValidation();
    if (store.edit?.errors.length !== 0) {
      store.edit?.setPreEditValid(true);
      return;
    }

    try {
      onCommit();
    } finally {
      store.edit?.setPreEditValid(true);
    }
  });

  return (
    <Wrapper>
      <Modal visible={disabled ?? false}>
        <Card>患者プロフィールを登録しています...</Card>
      </Modal>
      <DocumentTitle title="患者プロフィール登録" />
      <Caption type="header" underline align="center">
        患者プロフィール登録
      </Caption>
      <CaptionGroup>
        <Caption type="body">
          感染症対策及び待ち時間短縮のため、初めてご来院の方全員にご登録をお願いしています。
        </Caption>
      </CaptionGroup>
      <FormLayout>
        <FormItem gutter={4}>
          <FormLabel>資格確認書</FormLabel>
          <FormSubLabel>
            資格確認書をお持ちの方は、必ず来院前の資格確認書の表面写真のアップロードにご協力をお願いいたします。
            <br />
            尚、ご来院当日もご本人確認のため資格確認書を忘れずにご持参ください。
            <br />
            <span style={{ color: Color.FunctionalRedNeutral }}>
              ※マイナ保険証をお持ちの方は、事前のアップロードは不要です。当日のご持参のみお願いします。
              <br />
              ※マイナ保険証または資格確認書をお持ちでない場合には、全額自己負担となりますのでご注意ください。
            </span>
          </FormSubLabel>
          <Card
            smallPadding
            color={store.edit?.insuranceCards.urls.length === 0 ? 'normal' : 'inset'}
            shadowType="none"
          >
            {store.edit && <InsuranceCardInput store={store.edit.insuranceCards} />}
          </Card>
        </FormItem>
        <FormItem>
          <FormLabel required>電話番号</FormLabel>
          <PhoneInput
            color="white"
            value={store.edit?.phone}
            onFocusout={() => onFocusOut('phone')}
            onChange={v => store.edit?.setPhone(v)}
            error={store.edit?.errors.some(e => e.field === 'phone')}
          />
          {dirty.phone &&
            store.edit?.errors
              .filter(e => e.field === 'phone')
              .map(e => <ErrorMessage key={e.error}>{e.error}</ErrorMessage>)}
        </FormItem>
        <FormItem>
          <FormLabel required>住所</FormLabel>
          <AddressInput
            value={
              store.edit?.address || {
                addressLine1: '',
                addressLine2: '',
                pref: '',
                zipCode: '',
              }
            }
            onChange={v => store.edit?.setAddress(v)}
            onFocusout={onFocusOut}
            store={store}
            dirty={dirty}
          />
        </FormItem>
      </FormLayout>
      <div className={buttonGroupStyle}>
        <ButtonGroup gutter={10}>
          <Button
            block
            disabled={disabled || !!validateStatus.running || !store.edit?.preEditValid}
            onClick={() => {
              validateProfile();
            }}
          >
            この内容で登録する
          </Button>
        </ButtonGroup>
      </div>
    </Wrapper>
  );
});

const buttonGroupStyle = css`
  margin-top: 16px;
`;
